import type { RefObject } from 'react'
import { useCallback, useEffect, useRef } from 'react'
import { useSidebarContext } from '../context'
import { COLLAPSED_WIDTH } from '../constants'

interface UseSidebarHoverOptions {
  readonly enabled?: boolean
  readonly hoverDelay?: number
}

interface UseSidebarHoverReturn {
  readonly sidebarRef: RefObject<HTMLDivElement | null>
  readonly handleMouseEnter: () => void
}

export function useSidebarHover(options: UseSidebarHoverOptions = {}): UseSidebarHoverReturn {
  const { enabled = true, hoverDelay = 100 } = options
  const { state, setHovering, dispatch } = useSidebarContext()
  const { isPinned, isExpanded } = state

  const sidebarRef = useRef<HTMLDivElement>(null)
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimeouts = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
  }, [])

  const handleMouseEnter = useCallback(() => {
    if (!enabled || isPinned) return

    clearTimeouts()
    hoverTimeoutRef.current = setTimeout(() => {
      setHovering(true)
      if (!isExpanded) {
        dispatch({ type: 'SET_EXPANDED', expanded: true })
      }
    }, hoverDelay)
  }, [enabled, isPinned, isExpanded, hoverDelay, clearTimeouts, setHovering, dispatch])

  useEffect(() => {
    if (!enabled || isPinned) return

    const handleGlobalMouseMove = (event: MouseEvent) => {
      if (sidebarRef.current) {
        const rect = sidebarRef.current.getBoundingClientRect()
        const isNearLeftEdge = event.clientX <= COLLAPSED_WIDTH
        const isOverSidebar =
          event.clientX >= rect.left &&
          event.clientX <= rect.right &&
          event.clientY >= rect.top &&
          event.clientY <= rect.bottom

        if (isNearLeftEdge && !isOverSidebar) {
          handleMouseEnter()
        }
      }
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      clearTimeouts()
    }
  }, [enabled, isPinned, handleMouseEnter, clearTimeouts])

  useEffect(() => {
    return () => clearTimeouts()
  }, [clearTimeouts])

  return {
    sidebarRef,
    handleMouseEnter,
  }
}
